/**
 * StatusBackslideModal.jsx — Confirmation modal shown before backsliding a job's status.
 *
 * Triggered when changing status from a "later" stage to an "earlier" one (e.g.
 * delivered → post_production, or anything → void). Shows:
 *   - Preserved rows (earned $ that will NOT be un-earned — protected by ledger rule)
 *   - Forfeited rows (only on void: originator share forfeits)
 *
 * On Continue: calls onConfirm() which proceeds with the actual update.
 */

import { fmtC } from '../../lib/utils';

const ROLE_LABEL = {
  originator:  'Originator',
  prod_lead:   'Production Lead',
  prod_hourly: 'Production',
  post_lead:   'Post Lead',
  post_hourly: 'Post',
};

export default function StatusBackslideModal({ preview, fromStatus, toStatus, onConfirm, onCancel, isLoading }) {
  if (!preview) return null;
  const { reason, preserved = [], forfeited = [] } = preview;
  const sumPreserved = preserved.reduce((s, r) => s + r.amount, 0);
  const sumForfeited = forfeited.reduce((s, r) => s + r.amount, 0);
  const isVoid = reason === 'void';

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center" onClick={onCancel}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-surface border border-border rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[85dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b border-border">
          <h3 className="font-heading text-text-primary text-base">
            {isVoid ? 'Confirm void' : 'Status backslide warning'}
          </h3>
          <div className="text-2xs text-text-dim mt-0.5 font-mono">
            {fromStatus} → {toStatus}
          </div>
        </div>

        <div className="px-5 py-4 space-y-4 text-sm">
          {preserved.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-2xs uppercase tracking-wider text-green font-mono">Protected (stays earned)</div>
                <div className="font-mono text-green">{fmtC(sumPreserved)}</div>
              </div>
              <div className="text-2xs text-text-dim mb-2">
                These earnings already triggered and will <span className="text-text-primary">not</span> be un-earned.
              </div>
              <div className="space-y-1">
                {preserved.map(r => (
                  <div key={r.id} className="flex items-center justify-between text-xs py-1 px-2 bg-surface-2/50 rounded">
                    <div>
                      <span className="text-text-primary">{r.person}</span>
                      <span className="text-text-dim ml-2">{ROLE_LABEL[r.role] || r.role}</span>
                    </div>
                    <div className="font-mono text-text-primary">{fmtC(r.amount)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {forfeited.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-2xs uppercase tracking-wider text-red-accent font-mono">Will forfeit</div>
                <div className="font-mono text-red-accent">{fmtC(sumForfeited)}</div>
              </div>
              <div className="text-2xs text-text-dim mb-2">
                Originator earnings forfeit on void (per company policy).
              </div>
              <div className="space-y-1">
                {forfeited.map(r => (
                  <div key={r.id} className="flex items-center justify-between text-xs py-1 px-2 bg-surface-2/50 rounded">
                    <div>
                      <span className="text-text-primary">{r.person}</span>
                      <span className="text-text-dim ml-2">{ROLE_LABEL[r.role] || r.role}</span>
                    </div>
                    <div className="font-mono text-red-accent">−{fmtC(r.amount)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {preserved.length === 0 && forfeited.length === 0 && (
            <div className="text-text-dim text-xs">No earnings to flag — safe to proceed.</div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-3 py-1.5 bg-surface border border-border text-text-primary text-xs font-semibold rounded hover:bg-surface-2 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`px-3 py-1.5 text-bg text-xs font-semibold rounded transition-colors disabled:opacity-50 ${isVoid ? 'bg-red-accent hover:opacity-80' : 'bg-accent hover:bg-accent-hover'}`}
          >
            {isLoading ? 'Working…' : (isVoid ? 'Confirm Void' : 'Continue')}
          </button>
        </div>
      </div>
    </div>
  );
}
